import React from "react";
import { useQuery } from "@tanstack/react-query";
import { PageHeader } from "@/components/Layout";
import { api } from "@/lib/api";
import { useSettings } from "@/lib/useSettings";
import { CheckCircle2 } from "lucide-react";

export default function About() {
    const { data: settings } = useSettings();
    const trusteesQ = useQuery({ queryKey: ["trustees"], queryFn: () => api.get("/trustees").then((r) => r.data) });
    const trustees = trusteesQ.data || [];
    const s = settings || {};

    return (
        <div data-testid="about-page">
            <PageHeader
                eyebrow="About Tishya"
                title={<>A trust rooted in <span className="text-secondary italic font-light">Lucknow</span>, working for the country.</>}
                sub="Established as a charitable trust, Tishya Foundation channels community goodwill into measurable, on-ground impact across four focus areas."
                image="https://images.pexels.com/photos/10326904/pexels-photo-10326904.jpeg?auto=compress&cs=tinysrgb&w=940"
            />

            {/* Vision / Mission */}
            <section className="mx-auto max-w-7xl px-5 lg:px-10 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
                <div className="lg:col-span-6 bg-card border border-border p-8 lg:p-12">
                    <div className="font-eyebrow text-xs text-secondary mb-4">Our Vision</div>
                    <p className="font-display text-2xl lg:text-3xl leading-snug">{s.vision}</p>
                </div>
                <div className="lg:col-span-6 bg-primary text-primary-foreground p-8 lg:p-12">
                    <div className="font-eyebrow text-xs text-secondary mb-4">Our Mission</div>
                    <p className="font-display text-2xl lg:text-3xl leading-snug">{s.mission}</p>
                </div>
            </section>

            {/* Values */}
            <section className="bg-muted/30 border-y border-border/60">
                <div className="mx-auto max-w-7xl px-5 lg:px-10 py-20">
                    <div className="font-eyebrow text-xs text-secondary mb-3">Our Values</div>
                    <h2 className="font-display text-3xl lg:text-5xl tracking-tight mb-10 max-w-2xl">
                        Five things we refuse to compromise on.
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
                        {(s.values || []).map((v, i) => (
                            <div key={v} className="bg-background border border-border p-6 hover:-translate-y-1 transition-transform">
                                <div className="font-eyebrow text-[10px] text-muted-foreground mb-3">0{i + 1}</div>
                                <CheckCircle2 className="h-5 w-5 text-secondary mb-3" />
                                <div className="font-display text-lg">{v}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Trustees */}
            <section className="mx-auto max-w-7xl px-5 lg:px-10 py-20">
                <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
                    <div>
                        <div className="font-eyebrow text-xs text-secondary mb-3">Board of Trustees</div>
                        <h2 className="font-display text-3xl lg:text-5xl tracking-tight">
                            The hands behind the mission.
                        </h2>
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                    {trustees.map((t) => (
                        <div key={t.id} data-testid={`trustee-${t.id}`} className="group">
                            <div className="aspect-[3/4] overflow-hidden bg-muted">
                                <img
                                    src={t.image_url || "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=600"}
                                    alt={t.name}
                                    loading="lazy"
                                    className="w-full h-full object-cover grayscale-[20%] group-hover:grayscale-0 transition-all duration-700"
                                />
                            </div>
                            <div className="mt-4">
                                <div className="font-eyebrow text-[10px] text-secondary mb-1">{t.role}</div>
                                <h3 className="font-display text-xl">{t.name}</h3>
                                {t.bio && <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{t.bio}</p>}
                            </div>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
}
